import { clearTreatmentCoursePatientParams } from './treatmentCoursePatientParams';

export const REFERRAL_COMPOSE_TYPES = ['sygehus', 'billeddiagnostik', 'speciallaege'] as const;
export type ReferralComposeType = (typeof REFERRAL_COMPOSE_TYPES)[number];

export type HenvisningBox = 'sendte' | 'modtagne';

/** Opening Henvisning shows Modtagne. Sendte is the explicit `?vis=sendte` view. */
export const DEFAULT_HENVISNING_BOX: HenvisningBox = 'modtagne';

export function henvisningBoxFromSearch(search: Pick<URLSearchParams, 'get'>): HenvisningBox {
  return search.get('vis') === 'sendte' ? 'sendte' : DEFAULT_HENVISNING_BOX;
}

export type PatientRecordView =
  | { kind: 'tab' }
  | { kind: 'journal-write' }
  | { kind: 'henvisning-list'; box: HenvisningBox; createdId: string | null }
  | { kind: 'henvisning-new'; type: ReferralComposeType }
  | { kind: 'henvisning-detail'; outboxId: string }
  | { kind: 'correspondence-new' }
  | { kind: 'ggop-new' };

export function patientRecordPath(patientId: string): string {
  return `/patients/${patientId}`;
}

export function patientTabPath(patientId: string, tab: string): string {
  return `${patientRecordPath(patientId)}?tab=${encodeURIComponent(tab)}`;
}

export function patientHenvisningPath(
  patientId: string,
  opts?: { vis?: HenvisningBox; created?: string },
): string {
  const params = new URLSearchParams();
  if (opts?.vis === 'sendte') params.set('vis', 'sendte');
  if (opts?.created) params.set('created', opts.created);
  const query = params.toString();
  return `${patientRecordPath(patientId)}/henvisning${query ? `?${query}` : ''}`;
}

export function patientHenvisningNewPath(patientId: string, type: ReferralComposeType): string {
  return `${patientRecordPath(patientId)}/henvisning/ny?type=${type}`;
}

export function patientHenvisningDetailPath(patientId: string, outboxId: string): string {
  return `${patientRecordPath(patientId)}/henvisning/${outboxId}`;
}

export function henvisningCreatedDetailPath(patientId: string, outboxId: string): string {
  return `${patientHenvisningDetailPath(patientId, outboxId)}?created=1`;
}

export function patientCorrespondenceNewPath(patientId: string): string {
  return `${patientRecordPath(patientId)}/korrespondance/ny`;
}

export function patientGgopNewPath(patientId: string): string {
  return `${patientRecordPath(patientId)}/ggop/ny`;
}

function isComposeType(value: string | null): value is ReferralComposeType {
  return value === 'sygehus' || value === 'billeddiagnostik' || value === 'speciallaege';
}

/** Nested patient-record path, or `{ kind: 'tab' }` for `?tab=` on `/patients/:id`. */
export function parsePatientRecordPath(
  pathname: string,
  search: URLSearchParams,
): PatientRecordView {
  const parts = pathname.split('/').filter(Boolean);
  const root = parts[0];
  if (root !== 'patients' && root !== 'patienter') return { kind: 'tab' };
  const rest = parts.slice(2);
  if (rest[0] === 'henvisning') {
    if (rest[1] === 'ny') {
      const type = search.get('type');
      return { kind: 'henvisning-new', type: isComposeType(type) ? type : 'sygehus' };
    }
    if (rest[1]) return { kind: 'henvisning-detail', outboxId: rest[1] };
    return {
      kind: 'henvisning-list',
      box: henvisningBoxFromSearch(search),
      createdId: search.get('created'),
    };
  }
  if (rest[0] === 'journal' && rest[1] === 'skriv') return { kind: 'journal-write' };
  if (rest[0] === 'korrespondance' && rest[1] === 'ny') return { kind: 'correspondence-new' };
  if (rest[0] === 'ggop' && rest[1] === 'ny') return { kind: 'ggop-new' };
  return { kind: 'tab' };
}

/**
 * Query string after the user clicks a patient tab (Henvisning is a path and
 * is handled separately).
 *
 * Journal and communication keep their own deep-link params when that tab is
 * clicked again. Behandlingsforløb does not: `?course=` / `?step=` is a
 * subview of the same tab, so clicking Behandlingsforløb always opens the
 * course list — including a second click while a course, step, or editor is open.
 */
export function patientTabClickSearchParams(
  current: URLSearchParams | undefined,
  tab: string,
): URLSearchParams {
  const next = clearTreatmentCoursePatientParams(
    current ? new URLSearchParams(current) : new URLSearchParams(),
  );
  next.set('tab', tab);
  next.delete('vis');
  next.delete('created');
  next.delete('type');
  if (tab !== 'journal') {
    next.delete('appointment');
    next.delete('entry');
    next.delete('date');
  }
  if (tab !== 'communication') {
    next.delete('channel');
    next.delete('message');
  }
  return next;
}

export function patientRecordActiveTab(
  view: PatientRecordView,
): 'henvisning' | 'medcom' | 'journal' | null {
  switch (view.kind) {
    case 'henvisning-list':
    case 'henvisning-new':
    case 'henvisning-detail':
      return 'henvisning';
    case 'correspondence-new':
    case 'ggop-new':
      return 'medcom';
    case 'journal-write':
      return 'journal';
    case 'tab':
      return null;
    default: {
      const unexpected: never = view;
      return unexpected;
    }
  }
}
